/**
 * Rent vs. Buy Calculator
 *
 * DEBUG URL
 * https://coastal1.test/rent-vs-buy-calculator/?logged-out&monthly-rent-price=2500&zip-code=02030&lead-name=Kevin Leary&lead-email=coastal1@kevinleary.net&lead-phone=7815901000&lead-car-loans=500&lead-student-loans=1000&lead-credit-cards=75&lead-personal-loans=250
 */
import { defaultsDeep, some, isEmpty, toNumber, isNumber, round, isEqual, each } from "lodash-es";
import "../vendor/select2.min";
import "./_serialize-object";
import { numberToCurrency, markFieldAsValid, markFieldAsInvalid, trackEvent, numberFormat, findPropertyTaxForZipCode, dollarConfig, percentageConfig, customDropdowns, PMT, PV, FV, CUMIPMT, usd } from "./_calculators.js";
const $ = window.jQuery;
const dataLayer = window.dataLayer;

class CalculatorsRentVsBuy {
  // Tracks error count
  errors = 0;
  calculation = {};
  debug = {};

  // Initialize
  constructor() {
    customDropdowns();
    this.defineInputs();
    this.formHandler();
    this.createChart();
    this.initialState();
  }

  // Advanced number inputs
  defineInputs() {
    // Calc inputs
    this.monthlyRentPrice = new AutoNumeric("#monthly-rent-price", dollarConfig);
    this.interestRate = new AutoNumeric("#interest-rate", percentageConfig);
    this.loanTerm = $("#loan-term");
    this.estimatedTaxes = new AutoNumeric("#estimated-taxes", dollarConfig);
    this.homeOwnersInsurance = new AutoNumeric("#home-owners-insurance", dollarConfig);
    this.hoaFees = new AutoNumeric("#hoa-fees", dollarConfig);
    this.downPayment = new AutoNumeric(
      "#down-payment",
      defaultsDeep(
        {
          decimalPlaces: 1,
          decimalPlacesRawValue: 1,
        },
        percentageConfig
      )
    );
  }

  // Fade-in onload
  initialState() {
    $(() => {
      // Calculate onload for debugging purposes
      this.calculateMortgageFromRent();
      $(".calculator").addClass("ready");
    });
  }

  // Calulator form validation
  calculatorFormIsValid() {
    // Input values
    const monthlyRentPrice = this.monthlyRentPrice.rawValue;
    const interestRate = this.interestRate.rawValue;
    const loanTerm = this.loanTerm.val();
    const estimatedTaxes = this.estimatedTaxes.rawValue;
    const homeOwnersInsurance = this.homeOwnersInsurance.rawValue;
    const hoaFees = this.hoaFees.rawValue;
    const downPayment = this.downPayment.rawValue;

    // Required fields are empty
    if (some([monthlyRentPrice, interestRate, loanTerm, estimatedTaxes, homeOwnersInsurance, hoaFees, downPayment], isEmpty)) {
      console.log("Required fields are empty", [monthlyRentPrice, interestRate, loanTerm, estimatedTaxes, homeOwnersInsurance, hoaFees, downPayment]);
      return false;
    }

    // Invalid number
    if (monthlyRentPrice <= 0) return false;
    if (interestRate <= 0) return false;
    if (estimatedTaxes <= 0) return false;

    return true;
  }

  // Submit calculator form
  submitForm = (event) => {
    const element = $(event.delegateTarget);
    const id = element.attr("id");

    // #monthly-rent-price
    if (id === "monthly-rent-price") {
      if (this.monthlyRentPrice.rawValue <= 0) {
        markFieldAsInvalid(element);
        return;
      } else {
        markFieldAsValid(element);
      }
    }

    // #interest-rate
    if (id === "interest-rate") {
      if (this.interestRate.rawValue <= 0) {
        markFieldAsInvalid(element);
        return;
      } else {
        markFieldAsValid(element);
      }
    }

    // #estimated-taxes
    if (id === "estimated-taxes") {
      if (this.estimatedTaxes.rawValue <= 0) {
        markFieldAsInvalid(element);
        return;
      } else {
        markFieldAsValid(element);
      }
    }

    // Calculate results
    this.calculateMortgageFromRent();
  };

  // Convert rent price to mortgage amount
  async calculateMortgageFromRent() {
    if (!this.calculatorFormIsValid()) return;

    // Input values
    const rent = toNumber(this.monthlyRentPrice.rawValue);
    const interest = toNumber(this.interestRate.rawValue);
    const years = toNumber(this.loanTerm.val());
    const taxes = toNumber(this.estimatedTaxes.rawValue);
    const insurance = toNumber(this.homeOwnersInsurance.rawValue);
    const hoa = toNumber(this.hoaFees.rawValue);
    const percentDown = toNumber(this.downPayment.rawValue);

    // Monthly amounts
    const monthlyTaxes = taxes / 12;
    const monthlyInsurance = insurance / 12;
    const reduction = monthlyTaxes + monthlyInsurance + hoa;
    const rentLessDebt = rent - reduction;
    const annualHoa = hoa * 12;

    // Compounded interest equation
    let i = interest;
    if (i >= 1.0) {
      i = i / 100.0;
    }
    i /= 12;

    const totalNumOfPayments = years * 12;
    const percDownFactor = (100 - percentDown) / 100;

    let pow = 1;
    for (let j = 0; j < totalNumOfPayments; j++) {
      pow = pow * (1 + i);
    }

    const principal = ((pow - 1) * rentLessDebt) / (pow * i);
    const homePrice = principal / percDownFactor;
    const downPayment = homePrice - principal;

    // PMI
    let pmiPerc = 0;
    if (percentDown < 20) pmiPerc = 0.005;
    if (percentDown < 15) pmiPerc = 0.01;
    if (percentDown < 10) pmiPerc = 0.015;
    if (percentDown < 5) pmiPerc = 0.02;
    const pmi = (pmiPerc * homePrice) / 12;

    // Convert interest from a percentage to a decimal, and convert from
    // an annual rate to a monthly rate. Convert payment period in years
    // to the number of monthly payments.
    let monthlyInterestRate = interest / 100 / 12;
    let totalMonthlyPaymentCount = years * 12;
    let monthlyInterestPayment = monthlyInterestRate * principal;

    // Now compute the monthly payment figure, using esoteric math.
    let x = Math.pow(1 + monthlyInterestRate, totalMonthlyPaymentCount);
    let monthlyPaymentFigure = (principal * x * monthlyInterestRate) / (x - 1);
    let monthlyPayment = round(monthlyPaymentFigure, 2);
    let monthlyInterest = round(monthlyInterestPayment, 2);
    let monthlyPrincipal = round(monthlyPayment - monthlyInterest, 2);

    // Check that the result is a finite number. If so, display the results.
    if (!isNumber(monthlyPaymentFigure)) {
      console.error("monthlyPaymentFigure is not a valid number", monthlyPaymentFigure);
      return false;
    }

    // Debug output for math verifications
    this.debug = {};
    this.debug["Monthly PMI"] = pmi;
    this.debug["Monthly Rent"] = rent;
    this.debug["Monthly Taxes"] = monthlyTaxes;
    this.debug["Monthly Insurance"] = monthlyInsurance;
    this.debug["Monthly Rent Excluding Debt"] = rentLessDebt;
    this.debug["Monthly Mortgage Payment"] = monthlyPayment;
    this.debug["Monthly Mortgage Interest"] = monthlyInterest;
    this.debug["Monthly Mortgage Principal"] = monthlyPrincipal;
    this.debug["PMI Rate"] = pmiPerc * 100 + "%";
    this.debug["Percent Down"] = percentDown;
    this.debug["Down Payment"] = downPayment;
    this.debug["Mortgage Amount"] = principal;
    this.debug["Home Price"] = homePrice;
    console.log("Calculation Details", this.debug);

    const newCalculation = {
      homePrice: homePrice,
      principal: monthlyPrincipal,
      interest: monthlyInterest,
      insurance: round(monthlyInsurance, 2),
      hoa: round(hoa, 2),
      taxes: round(monthlyTaxes, 2),
      pmi: round(pmi, 2),
    };

    // Test for duplicate
    if (isEqual(this.calculation, newCalculation)) return;

    // Track analytics event: category, action, label
    const eventAction = rent;
    const eventValue = usd(this.calculation.homePrice - downPayment);
    const eventLabel = usd(this.calculation.homePrice);
    trackEvent("Rent vs. Buy Calculator Submitted", eventAction, eventLabel, eventValue);

    // Update chart with new data
    this.calculation = newCalculation;
    this.updateChart();
  }

  // Form submit handler
  formHandler() {
    $('form[data-calculator="rent-vs-buy"]').on("submit", (event) => {
      event.preventDefault();
    });

    $('form[data-calculator="rent-vs-buy"] input, form[data-calculator="rent-vs-buy"] select').on("change", (event) => {
      this.submitForm(event);
    });
  }

  // Update chart values
  updateChart(preview) {
    // Default preview off
    if (preview === undefined) preview = false;

    // Create initial data values
    if (this.chart.data.length === 0) {
      this.chart.data = [
        {
          label: "Principal",
          cost: this.calculation.principal,
          color: "#00529f",
        },
        {
          label: "Interest",
          cost: this.calculation.interest,
          color: "#FFDC00",
        },
        {
          label: "Taxes",
          cost: this.calculation.taxes,
          color: "#95C12A",
        },
        {
          label: "Insurance",
          cost: this.calculation.insurance,
          color: "#4D9DE0",
        },
        {
          label: "HOA Fees",
          cost: this.calculation.hoa,
          color: "#D32A2A",
        },
        {
          label: "PMI",
          cost: this.calculation.pmi,
          color: "#FA7921",
        },
      ];
    }

    // Update existing values
    else {
      this.chart.data[0].cost = this.calculation.principal;
      this.chart.data[1].cost = this.calculation.interest;
      this.chart.data[2].cost = this.calculation.taxes;
      this.chart.data[3].cost = this.calculation.insurance;
      this.chart.data[4].cost = this.calculation.hoa;
      this.chart.data[5].cost = this.calculation.pmi;
      this.chart.invalidateRawData();
    }

    // Update legend
    const $legend = $("#chart-legend");
    let legendHtml = "";
    each(this.chart.data, (item, index) => {
      legendHtml += `<p class="calculator__legend__row">
        <span class="calculator__legend__marker" style="background-color:${item.color}"></span>
        <span class="calculator__legend__label">${item.label}</span>
        <span class="calculator__legend__value">$${item.cost}</span>
      </p>`;
    });
    $legend.hide().empty().html(legendHtml).fadeIn(500);

    // Update total monthly price
    if (!preview) {
      const monthlyPaymentAmt = usd(this.calculation.principal + this.calculation.interest + this.calculation.taxes + this.calculation.pmi);
      $("#monthly-payment-amt").addClass("hidden");
      $("#monthly-payment-amt > span").text(monthlyPaymentAmt);
      setTimeout(() => {
        $("#monthly-payment-amt").removeClass("hidden");
      }, 500);
    } else {
      $("#monthly-payment-amt").removeClass("hidden");
    }

    // Update recommended home price
    const formattedHomePrice = usd(this.calculation.homePrice);
    if (!preview) {
      const existingHomePrice = $("#home-price-estimate").text();
      if (formattedHomePrice !== existingHomePrice) {
        $("#home-price-estimate").hide().text(formattedHomePrice).delay(500).fadeIn(1000);
      }
    } else {
      $("#home-price-estimate").hide().text(formattedHomePrice).delay(500).fadeIn(1000);
      $(".calculator__monthly-payment").hide().fadeIn(1000);
    }
  }

  // Display chart
  createChart() {
    am4core.ready(() => {
      am4core.useTheme(am4themes_animated);

      // Container
      var container = am4core.create("rent-vs-buy-chart", am4core.Container);
      container.width = am4core.percent(100);
      container.height = am4core.percent(100);
      container.layout = "horizontal";
      container.fontFamily = "Roboto";

      // Chart
      this.chart = container.createChild(am4charts.PieChart);
      this.chart.paddingTop = 0;
      this.chart.fontSize = 14;
      this.chart.focusable = false;

      // Series
      this.series = this.chart.series.push(new am4charts.PieSeries());

      // Animations
      this.series.hiddenState.properties.endAngle = -90;
      this.series.hiddenState.properties.startAngle = -90;
      this.series.hiddenState.transitionDuration = 1000;
      this.series.defaultState.transitionDuration = 1000;
      this.series.interpolationDuration = 1000;

      // Labels
      //this.series.labels.template.fill = am4core.color( "#3d3d3d" );
      //this.series.labels.template.radius = 10;
      this.series.labels.template.disabled = true;
      this.series.ticks.template.disabled = true;

      // Legend
      this.series.dataFields.value = "cost";
      this.series.dataFields.depthValue = "cost";
      this.series.dataFields.category = "label";

      // Colors
      this.series.colors.list = [am4core.color("#00529f"), am4core.color("#FFDC00"), am4core.color("#95C12A"), am4core.color("#4D9DE0"), am4core.color("#D32A2A"), am4core.color("#FA7921")];

      // Styles
      this.series.slices.template.stroke = am4core.color("#fff");
      this.series.slices.template.strokeWidth = 3;
      this.series.slices.template.strokeOpacity = 1;
      this.series.slices.template.cornerRadius = 5;
      this.series.slices.template.tooltipText = "";
      this.series.slices.template.clickable = false;
      this.series.slices.template.hoverable = false;
      this.series.slices.template.focusable = false;
      this.series.slices.template.clickable = false;
      this.series.slices.template.wheelable = false;
      this.series.slices.template.togglable = false;
      this.series.slices.template.trackable = false;
      this.series.slices.template.swipeable = false;
      this.series.slices.template.interactionsEnabled = false;
      this.series.slices.template.tabindex = 99999;

      // States
      var hoverState = this.series.slices.template.states.create("hover");
      hoverState.properties.scale = 1;
      var activeState = this.series.slices.template.states.create("active");
      activeState.properties.shiftRadius = 0;

      // Initial display data
      this.calculation = {
        homePrice: 574440.37,
        principal: 755.38,
        interest: 1244.62,
        taxes: 541.67,
        insurance: 83.33,
        hoa: 0,
        pmi: 0,
      };

      this.updateChart(true);
    }); // end am4core.ready()
  }
}

new CalculatorsRentVsBuy();
